import { Menu } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ManagerBottomNavigationBar from '../../../components/ManagerBottomNavigationBar';
import ManagerUserCard from '../../../components/ManagerUserCard';
import ManagerUserEffectsModal from '../../../components/ManagerUserEffectsModal';
import ManagerUserInventoryModal from '../../../components/ManagerUserInventoryModal';
import ManagerUserTransactionsModal from '../../../components/ManagerUserTransactionsModal';
import SidebarMenu from '../../../components/SidebarMenu';
import StickersManagementModal from '../../../components/StickersManagementModal';
import type { User } from '../../../service/api';
import useManagerUsersStore from '../../../store/managerUsers';

export default function ManagerUsersPage() {
  const { t } = useTranslation();
  const [showSidebar, setShowSidebar] = useState(false);
  const {
    users,
    userBalances,
    isLoading,
    error,
    fetchUsers,
    updateUserBalance,
  } = useManagerUsersStore();

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showStickersModal, setShowStickersModal] = useState(false);
  const [transactionsUser, setTransactionsUser] = useState<User | null>(null);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [inventoryUser, setInventoryUser] = useState<User | null>(null);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [effectsUser, setEffectsUser] = useState<User | null>(null);
  const [showEffectsModal, setShowEffectsModal] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleManageStickers = (user: User) => {
    setSelectedUser(user);
    setShowStickersModal(true);
  };

  const handleCloseModal = () => {
    setShowStickersModal(false);
    setSelectedUser(null);
  };

  const handleViewTransactions = (user: User) => {
    setTransactionsUser(user);
    setShowTransactionsModal(true);
  };

  const handleCloseTransactions = () => {
    setShowTransactionsModal(false);
    setTransactionsUser(null);
  };

  const handleManageInventory = (user: User) => {
    setInventoryUser(user);
    setShowInventoryModal(true);
  };

  const handleCloseInventory = () => {
    setShowInventoryModal(false);
    setInventoryUser(null);
  };

  const handleManageEffects = (user: User) => {
    setEffectsUser(user);
    setShowEffectsModal(true);
  };

  const handleCloseEffects = () => {
    setShowEffectsModal(false);
    setEffectsUser(null);
  };

  const handleUpdateStickers = async (amount: number) => {
    if (!selectedUser) return;

    try {
      await updateUserBalance(selectedUser._id, amount);
      handleCloseModal();
    } catch (error) {
      console.error('Failed to update stickers:', error);
    }
  };

  return (
    <div className="bg-white min-vh-100 d-flex flex-column">
      <Container fluid className="flex-grow-1 pb-5">
        {/* Sticky Header */}
        <div
          className="sticky-top bg-white"
          style={{
            zIndex: 1020,
            borderBottom: '1px solid rgba(0,0,0,0.1)',
          }}
        >
          <Row className="align-items-center p-4 pb-2">
            <Col xs="auto">
              <Menu
                size={24}
                className="text-dark"
                onClick={() => setShowSidebar(true)}
                style={{ cursor: 'pointer' }}
              />
            </Col>
            <Col className="text-center">
              <h2 className="text-dark h5 fw-bold mb-0">
                {t('manager.users.title')}
              </h2>
            </Col>
            <Col xs="auto" style={{ width: '24px' }} />
          </Row>
        </div>

        {/* Content */}
        <div className="px-0 pt-3">
          {isLoading && (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          )}

          {error && <Alert variant="danger">{error}</Alert>}

          {!isLoading && !error && users.length === 0 && (
            <p className="text-center text-muted py-4">
              {t('manager.users.noUsers')}
            </p>
          )}

          {!isLoading &&
            !error &&
            users.map(user => (
              <ManagerUserCard
                key={user._id}
                user={user}
                balance={userBalances[user._id] || 0}
                onManageStickers={() => handleManageStickers(user)}
                onViewTransactions={() => handleViewTransactions(user)}
                onManageInventory={() => handleManageInventory(user)}
                onManageEffects={() => handleManageEffects(user)}
              />
            ))}
        </div>
      </Container>

      <SidebarMenu show={showSidebar} onHide={() => setShowSidebar(false)} />
      <ManagerBottomNavigationBar />

      <StickersManagementModal
        show={showStickersModal}
        onHide={handleCloseModal}
        user={selectedUser}
        currentBalance={selectedUser ? userBalances[selectedUser._id] || 0 : 0}
        onConfirm={handleUpdateStickers}
      />

      <ManagerUserTransactionsModal
        show={showTransactionsModal}
        onHide={handleCloseTransactions}
        user={transactionsUser}
      />

      <ManagerUserInventoryModal
        show={showInventoryModal}
        onHide={handleCloseInventory}
        user={inventoryUser}
      />

      <ManagerUserEffectsModal
        show={showEffectsModal}
        onHide={handleCloseEffects}
        user={effectsUser}
      />
    </div>
  );
}
